<script setup lang="ts">
import type { ShakaErrorEvent } from '~/composables/useHlsPlayer/types';
import type { ShakaPlayer } from '~/composables/useSharedVideoPlayer';

import { useEventListener, useMounted, useTimeoutFn } from '@vueuse/core';
import { useFeatureFlags } from '~/composables/useFeatureFlags';
import {
	registerActivePlayer,
	unregisterActivePlayer
} from '~/composables/useHlsPlayer/shakaLoader';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { useVideoQoE } from '~/composables/useVideoQoE';
import { wasVideoIntentPrefetched } from '~/composables/useVideoWarmup';
import { ErrorCategory } from '~/core/ports/datadog';
import {
	SHORTS_AUTOPLAY_MUTED_EVENT,
	SHORTS_FIRST_FRAME_EVENT
} from '~/utils/shortsEvents';
import { useDatadog } from '~~/composables/useDatadog';

import {
	REEL_THUMB_FORMAT,
	REEL_THUMB_HEIGHT,
	REEL_THUMB_QUALITY,
	REEL_THUMB_SIZES,
	REEL_THUMB_WIDTH
} from '~/components/VideoCard/reelThumbnailPreset';

import IconMaterialSymbolsLightForward10 from '~icons/material-symbols-light/forward-10';
import IconMaterialSymbolsLightReplay10 from '~icons/material-symbols-light/replay-10';

import { getIOSHighQualityUrl } from './iosQualityCache';
import {
	isIOSDevice,
	isIOSNativePlayback,
	isSharedVideoElementBlessed,
	setIOSMsePreference,
	useIOSSharedVideo
} from './useIOSSharedVideo';
import { useVideoTouchGestures } from './useVideoTouchGestures';
import { useVideoUnmute } from './useVideoUnmute';

interface ReelVideoPlayerProps {
	videoUrl: string | null;
	thumbnailUrl: string | null;
	shouldLoad: boolean;
	isActive: boolean;
	isPreloadOnly: boolean;
	isMuted: boolean;
	index: number;
	seekFeedback?: 'forward' | 'backward' | null;
}

const props = withDefaults(defineProps<ReelVideoPlayerProps>(), {
	seekFeedback: null
});

const emit = defineEmits<{
	(e: 'video-loaded'): void;
	(e: 'video-playing'): void;
	(e: 'video-pause'): void;
	(e: 'video-buffered'): void;
	(e: 'video-error'): void;
	(e: 'loadedmetadata', duration: number): void;
	(e: 'timeupdate', currentTime: number): void;
	(e: 'ended'): void;
	(e: 'single-tap'): void;
	(e: 'double-tap'): void;
}>();

const { trackError } = useDatadog();

const isMounted = useMounted();

// MSE on iOS (ManagedMediaSource, iOS 17.1+) is gated by feature flag —
// preference must be set before the first isIOSNativePlayback() call below.
setIOSMsePreference(useFeatureFlags().ft_shorts_ios_mse);
// Playback pipeline decision: native AVFoundation vs Shaka/MSE. The shared
// blessed element (autoplay gesture) applies to both pipelines on iOS.
const useNativePlayback = isIOSNativePlayback();
const iosSharedVideo = isIOSDevice ? useIOSSharedVideo() : null;

const videoContainerRef = ref<HTMLElement | null>(null);
const videoElementRef = ref<HTMLVideoElement | null>(null);
const shakaPlayer = shallowRef<ShakaPlayer | null>(null);
const hasError = ref(false);
const isVideoLoaded = ref(false);
const isBuffering = ref(false);
const isThumbnailLoaded = ref(false);
const hasEmittedBuffered = ref(false);
const isAutoplayBlocked = ref(false);
const wasPlayingBeforeInactive = ref(false);

const hasPlayableSource = computed(() => Boolean(props.videoUrl?.trim()));

// ---------------------------------------------------------------------------
// QoE telemetry (startup / rebuffering / play time -> Datadog vitals + action)
// ---------------------------------------------------------------------------

const {
	beginAttempt: beginQoEAttempt,
	finalize: finalizeQoE,
	abort: abortQoE
} = useVideoQoE({
	videoElement: videoElementRef,
	surface: 'shorts_feed',
	pipeline: useNativePlayback
		? 'ios_native'
		: iosSharedVideo
			? 'ios_mse'
			: 'shaka',
	getVideoUrl: () => props.videoUrl,
	getShakaPlayer: () => shakaPlayer.value,
	wasWarmed: wasVideoIntentPrefetched
});

function beginQoEIfMeasurable() {
	if (props.isPreloadOnly || !hasPlayableSource.value) return;
	beginQoEAttempt();
}

// Pre-resolved IPX URL for the <video> poster attribute ONLY - the NuxtImg
// overlay must keep receiving the RAW url (its own props apply the same
// preset; feeding it a processed url would nest two IPX passes and miss the
// card's HTTP cache). The raw bucket url of a 4K source is a 650KB+
// full-resolution frame; this processed one is ~10x smaller.
const img = useImage();
const thumbnailUrl = computed(() => props.thumbnailUrl);
const posterThumbnailUrl = computed(() =>
	props.thumbnailUrl
		? img(props.thumbnailUrl, {
				format: REEL_THUMB_FORMAT,
				quality: REEL_THUMB_QUALITY,
				width: REEL_THUMB_WIDTH,
				height: REEL_THUMB_HEIGHT
			})
		: props.thumbnailUrl
);

// ---------------------------------------------------------------------------
// Touch gestures (tap / double-tap / hold)
// ---------------------------------------------------------------------------

const {
	isHolding,
	handleTouchStart,
	handleTouchEnd,
	handleTouchCancel,
	handleTap,
	cancelPendingTap,
	cleanup: cleanupGestures
} = useVideoTouchGestures({
	videoElementRef,
	isAutoplayBlocked,
	onSingleTap: () => emit('single-tap'),
	onDoubleTap: () => emit('double-tap')
});

// ---------------------------------------------------------------------------
// Unmute logic
// ---------------------------------------------------------------------------

const {
	hasUnmuteFailed,
	startUnmuteDelay,
	stopUnmuteDelay,
	reset: resetUnmute
} = useVideoUnmute({
	videoElementRef,
	isMuted: () => props.isMuted
});

// ---------------------------------------------------------------------------
// Shaka player init (non-iOS)
// ---------------------------------------------------------------------------

const { start: startInitPlayer, stop: stopInitPlayer } = useTimeoutFn(
	() => {
		initPlayer();
	},
	100,
	{ immediate: false }
);

const MIN_BUFFER_SECONDS = 3;

function checkBufferAndEmit() {
	if (hasEmittedBuffered.value || !videoElementRef.value) return;

	const video = videoElementRef.value;
	const buffered = video.buffered;
	if (buffered.length === 0) return;

	const currentTime = video.currentTime;
	for (let i = 0; i < buffered.length; i++) {
		if (currentTime >= buffered.start(i) && currentTime < buffered.end(i)) {
			const bufferedAhead = buffered.end(i) - currentTime;
			if (bufferedAhead >= MIN_BUFFER_SECONDS) {
				hasEmittedBuffered.value = true;
				emit('video-buffered');
				return;
			}
		}
	}
}

// ---------------------------------------------------------------------------
// iOS native HLS path — no Shaka, no async between gesture and play()
// Safari handles HLS natively when given <video src="manifest.m3u8">.
// This eliminates the async Shaka attach/load chain that breaks the
// user-activation context on iOS (especially in Low Power Mode).
// ---------------------------------------------------------------------------

function claimSharedVideo() {
	if (!iosSharedVideo?.videoElement.value || !videoContainerRef.value) return;
	const video = iosSharedVideo.videoElement.value;
	videoContainerRef.value.appendChild(video);
	videoElementRef.value = video;

	// The shared video may already have passed canplay/loadedmetadata before
	// the event listeners (attached via useEventListener watching videoElementRef)
	// were registered. Sync state from the element's current readyState.
	if (video.readyState >= HTMLMediaElement.HAVE_METADATA && video.duration) {
		emit('loadedmetadata', video.duration);
	}
	if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
		if (!isVideoLoaded.value) {
			isVideoLoaded.value = true;
			emit('video-loaded');
		}
	}
}

function releaseSharedVideo() {
	if (!iosSharedVideo?.videoElement.value) return;
	const video = iosSharedVideo.videoElement.value;
	if (videoContainerRef.value?.contains(video)) {
		video.pause();
		videoContainerRef.value.removeChild(video);
	}
	videoElementRef.value = null;
}

let currentNativeSrc = '';

function initNativePlayer() {
	// Guard the whole native pipeline, not just the call sites: with
	// ft_shorts_ios_mse active, iosSharedVideo is still defined on iOS but
	// playback goes through Shaka/MSE — setting a native HLS src on the same
	// element the player attaches to would corrupt it.
	if (!useNativePlayback) return;
	if (!import.meta.client || !props.shouldLoad || !hasPlayableSource.value)
		return;

	const url = props.videoUrl?.trim();
	if (!url) return;

	if (iosSharedVideo && !props.isPreloadOnly) {
		claimSharedVideo();
	}

	const video = videoElementRef.value;
	if (!video) return;

	// Use high-quality variant URL if prefetched while network was proven fast,
	// bypassing AVPlayer's conservative initial quality selection.
	const highQualityUrl = !props.isPreloadOnly
		? getIOSHighQualityUrl(url)
		: null;
	const srcToLoad = highQualityUrl ?? url;

	if (video.src === srcToLoad || currentNativeSrc === srcToLoad) return;
	currentNativeSrc = srcToLoad;

	video.src = srcToLoad;
	video.loop = false;
	video.muted = props.isPreloadOnly ? true : props.isMuted;
	video.preload = props.isPreloadOnly ? 'metadata' : 'auto';
}

function cleanupNativePlayer() {
	const video = videoElementRef.value;
	currentNativeSrc = '';

	if (video) {
		video.pause();
		video.removeAttribute('src');
		video.load();
	}

	if (iosSharedVideo) {
		releaseSharedVideo();
	}
}

// ---------------------------------------------------------------------------
// Non-iOS Shaka path
// ---------------------------------------------------------------------------

let initGeneration = 0;

// Tracks an in-progress async destroyPlayer call so new inits can await it.
const pendingDestroy = shallowRef<Promise<void> | null>(null);

async function initPlayer() {
	if (!import.meta.client || !props.shouldLoad || !hasPlayableSource.value)
		return;

	const manifestUrl = props.videoUrl?.trim();
	if (!manifestUrl) return;

	// iOS + MSE: attach Shaka to the shared blessed element so the autoplay
	// gesture authorization carries over (same technique as the native path).
	if (iosSharedVideo && !props.isPreloadOnly) {
		claimSharedVideo();
	}

	const myGeneration = ++initGeneration;

	// If a previous destroy is still running, wait for it to finish before
	// attaching a new Shaka instance to the same video element.
	if (pendingDestroy.value) {
		await pendingDestroy.value;
	}

	// Re-check after awaiting: a cleanup may have incremented initGeneration.
	if (myGeneration !== initGeneration) return;

	await nextTick();
	if (myGeneration !== initGeneration) return;
	if (!videoElementRef.value) return;

	try {
		const player = await createPlayer(manifestUrl, videoElementRef.value, {
			isPreload: props.isPreloadOnly
		});

		if (myGeneration !== initGeneration) {
			destroyPlayer(player, videoElementRef.value);
			return;
		}

		if (!videoElementRef.value) {
			destroyPlayer(player);
			return;
		}

		shakaPlayer.value = player;

		videoElementRef.value.loop = false;
		videoElementRef.value.muted = true;

		await nextTick();
		if (myGeneration !== initGeneration) return;

		// Configuration was part of createPlayer setup
	} catch (error) {
		if (myGeneration !== initGeneration) return;

		// AbortError = request cancelled intentionally (scroll, timeout) — not a real failure.
		const isAbort =
			(error instanceof DOMException && error.name === 'AbortError') ||
			(error instanceof Error && error.message.includes('aborted'));
		if (isAbort) return;

		const errorMsg = error instanceof Error ? error.message : String(error);
		const errorCode = (error as Error & { code?: number })?.code;
		const errorCategory = (error as Error & { category?: number })?.category;

		// Transient network failure on the INITIAL load (Shaka NETWORK category=1,
		// or a bare "network error" TypeError from fetch). The video itself is
		// intact — retry with exponential backoff before surfacing the error, so
		// a flaky CDN/connection doesn't turn into a user-visible playback failure.
		const isNetworkError =
			errorCategory === 1 || /network error/i.test(errorMsg);
		if (isNetworkError) {
			networkErrorCount++;
			const MAX_RETRIES = 4;
			if (networkErrorCount <= MAX_RETRIES) {
				// Exponential backoff: 500ms, 1s, 2s, 4s
				const delayMs = 500 * Math.pow(2, networkErrorCount - 1);
				console.warn(
					`[ReelVideoPlayer] Init network error, retrying in ${delayMs}ms (attempt ${networkErrorCount}/${MAX_RETRIES}):`,
					errorCode
				);
				clearTimeout(networkErrorReinitTimer ?? undefined);
				networkErrorReinitTimer = setTimeout(() => {
					networkErrorReinitTimer = null;
					if (props.shouldLoad && isMounted.value) startInitPlayer();
				}, delayMs);
				return;
			}
			// Retries exhausted — fall through to surface the error.
		}

		console.error('[ReelVideoPlayer] Init error:', {
			message: errorMsg,
			code: errorCode,
			videoUrl: manifestUrl,
			index: props.index,
			fullError: error,
			timestamp: new Date().toISOString()
		});

		trackError({
			message: `[ReelVideoPlayer] Init error (${errorCode}): ${errorMsg}`,
			source: 'source',
			category: ErrorCategory.UI,
			stack: (error as Error)?.stack
		});
		hasError.value = true;
	}
}

function cleanupPlayer(urlToUnregister = props.videoUrl) {
	if (urlToUnregister) unregisterActivePlayer(urlToUnregister);
	if (useNativePlayback) {
		cleanupNativePlayer();
		return;
	}
	initGeneration++;
	const playerToDestroy = shakaPlayer.value;
	const videoEl = videoElementRef.value;
	shakaPlayer.value = null;
	// Track the async destroy so initPlayer() can await it before starting a
	// new Shaka instance on the same video element.
	const destroy = destroyPlayer(playerToDestroy, videoEl).finally(() => {
		if (pendingDestroy.value === destroy) {
			pendingDestroy.value = null;
		}
	});
	pendingDestroy.value = destroy;
	// iOS + MSE: Shaka runs on the shared blessed element — release it back
	if (iosSharedVideo) {
		releaseSharedVideo();
	}
}

// Tracks consecutive network failures across reinit cycles — must live at
// component scope so it survives player destruction/recreation.
let networkErrorCount = 0;
let networkErrorReinitTimer: ReturnType<typeof setTimeout> | null = null;

function resetState() {
	isVideoLoaded.value = false;
	isBuffering.value = false;
	isThumbnailLoaded.value = false;
	hasError.value = false;
	hasEmittedBuffered.value = false;
	hasUnmuteFailed.value = false;
	networkErrorCount = 0;
}

// ---------------------------------------------------------------------------
// Play / Pause
// ---------------------------------------------------------------------------

let playGeneration = 0;

function play() {
	if (!import.meta.client || !videoElementRef.value) return;

	// If browser policy already blocked autoplay, don't silently retry.
	// The user must tap to play (userPlay resets isAutoplayBlocked first).
	if (isAutoplayBlocked.value) return;

	// Replays (parent restarts after 'ended') arrive here without any prop
	// change — reopen the attempt so they keep telemetry. Idempotent while
	// one is running; the composable tags post-ended reopens as replay and
	// skips their (meaningless, warm) startup vitals.
	if (props.isActive) beginQoEIfMeasurable();

	const myPlayGeneration = ++playGeneration;
	resetUnmute();
	const video = videoElementRef.value;

	video.muted = props.isMuted;

	const promise = video.play();
	if (promise) {
		promise
			.then(() => {
				if (myPlayGeneration !== playGeneration) return;
				// play succeeded — nothing extra needed
			})
			.catch((error: DOMException) => {
				if (myPlayGeneration !== playGeneration) return;
				// Only NotAllowedError is a real autoplay-policy block. AbortError
				// (interrupted by a new load/cleanup) and NotSupportedError (no
				// data yet — play() fires again on loadedmetadata) must NOT set
				// the sticky block, or the later attempt that would succeed gets
				// skipped by the early return above.
				if (error?.name !== 'NotAllowedError') return;
				// Policy negou play() COM som (entrada sem gesto). Fallback estilo
				// YouTube: tenta de novo mutado na hora — o frame aparece igual — e
				// liga o som sozinho no primeiro gesto do usuario (tap/scroll).
				// Sites com MEI alto no Chrome nem caem aqui: tocam com som direto.
				if (!video.muted) {
					video.muted = true;
					video
						.play()
						.then(() => {
							if (myPlayGeneration !== playGeneration) return;
							window.dispatchEvent(new Event(SHORTS_AUTOPLAY_MUTED_EVENT));
						})
						.catch(() => {
							if (myPlayGeneration !== playGeneration) return;
							isAutoplayBlocked.value = true;
							abortQoE();
						});
					return;
				}
				isAutoplayBlocked.value = true;
				// The QoE clock started at activation; with autoplay blocked it
				// would count the user's think-time until the tap. Discard —
				// userPlay() starts a fresh, honest attempt.
				abortQoE();
			});
	}
}

function pause() {
	if (!import.meta.client) return;
	wasPlayingBeforeInactive.value = false;
	videoElementRef.value?.pause();
}

// User-initiated play: clears the autoplay block then plays normally.
function userPlay() {
	isAutoplayBlocked.value = false;
	beginQoEIfMeasurable();
	play();
}

// ---------------------------------------------------------------------------
// Video element event listeners (shared between iOS native and Shaka paths)
// ---------------------------------------------------------------------------

useEventListener(videoElementRef, 'canplay', () => {
	if (!import.meta.client) return;

	isBuffering.value = false;

	if (!isVideoLoaded.value) {
		isVideoLoaded.value = true;
		emit('video-loaded');
	}
});

useEventListener(videoElementRef, 'playing', () => {
	cancelPendingTap();
	isVideoLoaded.value = true;
	isBuffering.value = false;
	if (props.videoUrl) registerActivePlayer(props.videoUrl);
	notifyFirstFramePlayed();
	emit('video-playing');
});

// Signals the GTM loader (deferred on shorts entries) that the visitor got
// what they came for — the tags may now use the pipe. Once per page.
let firstFrameNotified = false;
function notifyFirstFramePlayed() {
	if (firstFrameNotified || props.isPreloadOnly) return;
	firstFrameNotified = true;
	try {
		window.dispatchEvent(new Event(SHORTS_FIRST_FRAME_EVENT));
	} catch {
		// Best-effort signal — never interfere with playback.
	}
}

useEventListener(videoElementRef, 'pause', () => {
	cancelPendingTap();
	if (props.videoUrl) unregisterActivePlayer(props.videoUrl);
	emit('video-pause');
});

useEventListener(videoElementRef, 'waiting', () => {
	if (isVideoLoaded.value) {
		isBuffering.value = true;
	}
});

useEventListener(videoElementRef, 'error', () => {
	const video = videoElementRef.value;
	if (video?.error) {
		console.error('[ReelVideoPlayer] Video element error:', {
			errorCode: video.error.code,
			errorMessage: video.error.message,
			videoUrl: props.videoUrl,
			index: props.index,
			timestamp: new Date().toISOString()
		});
	}
	hasError.value = true;
	emit('video-error');
});

useEventListener(videoElementRef, 'loadedmetadata', () => {
	if (!import.meta.client || !videoElementRef.value) return;
	emit('loadedmetadata', videoElementRef.value.duration);
});

useEventListener(videoElementRef, 'progress', checkBufferAndEmit);
useEventListener(videoElementRef, 'canplaythrough', () => {
	if (!hasEmittedBuffered.value) {
		hasEmittedBuffered.value = true;
		emit('video-buffered');
	}
});

useEventListener(videoElementRef, 'timeupdate', () => {
	if (!import.meta.client || !videoElementRef.value) return;
	emit('timeupdate', videoElementRef.value.currentTime);
});

useEventListener(videoElementRef, 'ended', () => {
	if (!import.meta.client) return;
	emit('ended');
});

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

watch(
	() => props.isMuted,
	isMuted => {
		if (videoElementRef.value) {
			videoElementRef.value.muted = isMuted;
		}
	}
);

// Virtualized feed can swap the video of a live component — that is a new
// play attempt (beginAttempt closes the previous one as 'navigated').
watch(
	() => props.videoUrl,
	() => {
		if (props.isActive && props.shouldLoad) beginQoEIfMeasurable();
	}
);

watch(
	() => hasError.value,
	isError => {
		if (isError && import.meta.client) {
			finalizeQoE('error');
			console.error('[ReelVideoPlayer] Video playback error', {
				videoUrl: props.videoUrl,
				index: props.index,
				thumbnailUrl: props.thumbnailUrl,
				timestamp: new Date().toISOString()
			});
		}
	}
);

// Shaka error listener (non-iOS only)
watch(
	() => shakaPlayer.value,
	player => {
		if (!player || !import.meta.client || useNativePlayback) return;

		const handleShakaError = (event: ShakaErrorEvent) => {
			const detail = event.detail;

			// Network/timeout errors on slow connections are expected — destroy and
			// reinitialise silently. Shaka's EWMA will be lower after the timeout so
			// the next init should pick a lower quality variant.
			// player.load() cannot be called after a CRITICAL error — Shaka is in an
			// error state. We must destroy the player and let the watcher reinit it.
			const isNetworkError = detail?.category === 1; // NETWORK category
			if (isNetworkError) {
				networkErrorCount++;
				const MAX_RETRIES = 4;
				if (networkErrorCount > MAX_RETRIES) {
					// Too many consecutive network failures — surface the error
					hasError.value = true;
					trackError({
						message: `[ReelVideoPlayer] Network error exceeded max retries (${networkErrorCount}) | videoUrl=${props.videoUrl} | index=${props.index}`,
						source: 'source',
						category: ErrorCategory.UI
					});
					return;
				}
				// Exponential backoff: 500ms, 1s, 2s, 4s
				const delayMs = 500 * Math.pow(2, networkErrorCount - 1);
				console.warn(
					`[ReelVideoPlayer] Network error, reinitialising in ${delayMs}ms (attempt ${networkErrorCount}/${MAX_RETRIES}):`,
					detail?.code
				);
				clearTimeout(networkErrorReinitTimer ?? undefined);
				networkErrorReinitTimer = setTimeout(() => {
					networkErrorReinitTimer = null;
					cleanupPlayer();
					// shouldLoad watcher only fires on dependency changes — reinit explicitly
					if (props.shouldLoad && isMounted.value) {
						useNativePlayback
							? nextTick(() => initNativePlayer())
							: startInitPlayer();
					}
				}, delayMs);
				return;
			}

			console.error('[ReelVideoPlayer] Shaka error event:', {
				code: detail?.code,
				category: detail?.category,
				severity: detail?.severity,
				videoUrl: props.videoUrl,
				index: props.index,
				timestamp: new Date().toISOString()
			});

			if (detail?.severity !== 0) {
				hasError.value = true;
			}
		};

		try {
			player.addEventListener('error', handleShakaError);
		} catch (e) {
			console.error('[ReelVideoPlayer] Failed to add Shaka error listener:', e);
		}
	}
);

// iOS shared video: claim when the container div appears in the DOM
if (iosSharedVideo) {
	watch(videoContainerRef, container => {
		if (
			container &&
			props.isActive &&
			hasPlayableSource.value &&
			props.shouldLoad
		) {
			initNativePlayer();
		}
	});
}

watch(
	() => props.isActive,
	isActive => {
		if (isActive) {
			// Start the QoE clock at activation (before any async init) so the
			// startup vital covers the whole manifest+segment+decode chain.
			if (props.shouldLoad) beginQoEIfMeasurable();
			nextTick(() => {
				if (iosSharedVideo && hasPlayableSource.value && props.shouldLoad) {
					initNativePlayer();
				}
				// Configuration is part of createPlayer setup
				if (wasPlayingBeforeInactive.value && videoElementRef.value?.paused) {
					play();
					wasPlayingBeforeInactive.value = false;
				}
			});
		} else {
			// Deactivation ends the play session unconditionally. The paused
			// check below only decides whether to keep the shared element for
			// the last-frame visual — the parent may have paused the video
			// before this post-flush watcher runs, so paused does NOT imply
			// tap-pause, and an open attempt would leak onto the next reel.
			finalizeQoE('navigated');
			if (iosSharedVideo) {
				// If video is paused keep the shared video so the last frame
				// stays visible; if it's still playing, it's a real navigation
				// away, so release.
				if (videoElementRef.value?.paused) {
					videoElementRef.value.pause();
				} else {
					releaseSharedVideo();
					resetState();
				}
			} else if (videoElementRef.value && !videoElementRef.value.paused) {
				wasPlayingBeforeInactive.value = true;
				videoElementRef.value.pause();
			}
		}
	},
	{ flush: 'post', immediate: true }
);

watch(
	() => [props.shouldLoad, isMounted.value] as const,
	([shouldLoad, mounted]) => {
		if (shouldLoad && hasPlayableSource.value && mounted) {
			// isActive can arrive before shouldLoad — start the QoE clock here
			// too (beginAttempt is idempotent per URL).
			if (props.isActive) beginQoEIfMeasurable();
			if (useNativePlayback) {
				nextTick(() => initNativePlayer());
			} else {
				startInitPlayer();
			}
		} else if (!shouldLoad) {
			stopInitPlayer();
			if (videoElementRef.value && !videoElementRef.value.paused) {
				videoElementRef.value.pause();
			}
			cleanupPlayer();
			resetState();
		}
	},
	{ flush: 'post' }
);

// Reset error state and reinitialise when the video URL changes while the
// component is still active (shouldLoad=true). Without this, networkErrorCount
// from a previous video accumulates into the new video's failure budget.
watch(
	() => props.videoUrl,
	(newUrl, oldUrl) => {
		if (!newUrl || newUrl === oldUrl) return;
		resetState();
		cleanupPlayer();
		if (props.shouldLoad && isMounted.value) {
			if (useNativePlayback) {
				nextTick(() => initNativePlayer());
			} else {
				startInitPlayer();
			}
		}
	}
);

// iOS + MSE promotion (preload → active): the preload player is attached to
// the item's local <video>, which the template removes on promotion, while
// active playback must happen on the single shared blessed element — extra
// live video elements break autoplay under iOS low-power mode. Re-attach on
// promotion: cleanup destroys the preload player, and initPlayer (now with
// isPreloadOnly=false) claims the shared element; segments warmed during
// preload return from CacheStorage, so the re-buffer is cheap. Native and
// non-iOS pipelines are untouched — their promotion paths already work.
watch(
	() => props.isPreloadOnly,
	(isPreloadOnly, wasPreloadOnly) => {
		if (!iosSharedVideo || useNativePlayback) return;
		if (isPreloadOnly || !wasPreloadOnly) return;
		resetState();
		cleanupPlayer();
		if (props.shouldLoad && isMounted.value) {
			startInitPlayer();
		}
	},
	{ flush: 'post' }
);

onBeforeUnmount(() => {
	if (networkErrorReinitTimer) {
		clearTimeout(networkErrorReinitTimer);
		networkErrorReinitTimer = null;
	}
	stopUnmuteDelay();
	stopInitPlayer();
	cleanupGestures();
	cleanupPlayer();
});

defineExpose({
	videoElementRef,
	play,
	pause,
	userPlay,
	isAutoplayBlocked,
	isHolding
});
</script>

<template>
	<div
		class="absolute inset-0 cursor-pointer select-none"
		@click="handleTap"
		@touchstart.passive="handleTouchStart"
		@touchend.passive="handleTouchEnd"
		@touchcancel.passive="handleTouchCancel"
	>
		<div
			v-if="!isVideoLoaded || hasError || !shouldLoad || !hasPlayableSource"
			class="absolute inset-0 z-10"
		>
			<!-- Mesmo preset IPX do VideoCard: a URL idêntica faz o pôster do
			     card clicado sair do cache HTTP em vez de re-baixar como jpg. -->
			<NuxtImg
				v-if="thumbnailUrl"
				:src="thumbnailUrl ?? undefined"
				:alt="`Vídeo do imóvel`"
				:class="[
					'absolute inset-0 size-full object-cover transition-opacity duration-300',
					isThumbnailLoaded ? 'opacity-100' : 'opacity-0'
				]"
				:width="REEL_THUMB_WIDTH"
				:height="REEL_THUMB_HEIGHT"
				:sizes="REEL_THUMB_SIZES"
				:format="REEL_THUMB_FORMAT"
				:quality="REEL_THUMB_QUALITY"
				:loading="isActive || index === 0 ? 'eager' : 'lazy'"
				:fetchpriority="isActive || index === 0 ? 'high' : undefined"
				@load="isThumbnailLoaded = true"
			/>

			<div
				v-if="
					shouldLoad &&
					hasPlayableSource &&
					!isVideoLoaded &&
					!hasError &&
					!isAutoplayBlocked
				"
				class="absolute inset-x-0 bottom-0 z-20 p-1"
			>
				<div class="h-[2px] w-full overflow-hidden">
					<div class="h-full w-full animate-video-loading-bar bg-white" />
				</div>
			</div>

			<div
				v-if="hasError"
				class="absolute inset-0 flex items-center justify-center bg-black/60"
			>
				<span class="text-xs text-white/70">Erro ao carregar</span>
			</div>
		</div>

		<!-- iOS active item: the blessed shared element is moved into this container.
		     Also kept alive during effective pause (tap-pause) so the last video
		     frame stays visible instead of showing a loading indicator. -->
		<div
			v-if="
				iosSharedVideo &&
				(isActive || videoElementRef?.paused) &&
				isMounted &&
				shouldLoad &&
				hasPlayableSource
			"
			ref="videoContainerRef"
			class="absolute inset-0 size-full"
		/>

		<!-- Local <video> for non-iOS, or iOS preload-only (adjacent items) -->
		<video
			v-if="
				(!iosSharedVideo || isPreloadOnly) &&
				isMounted &&
				shouldLoad &&
				hasPlayableSource
			"
			ref="videoElementRef"
			:poster="posterThumbnailUrl ?? undefined"
			:preload="isPreloadOnly ? 'metadata' : 'auto'"
			muted
			playsinline
			class="size-full object-cover [&]:object-cover"
		/>

		<Transition
			enter-active-class="transition-opacity duration-200"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-300"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				v-if="isBuffering && isVideoLoaded"
				class="absolute inset-x-0 bottom-0 z-30 p-1"
			>
				<div class="h-[2px] w-full overflow-hidden">
					<div class="h-full w-full animate-video-loading-bar bg-white" />
				</div>
			</div>
		</Transition>

		<div
			v-if="!isHolding"
			class="pointer-events-none absolute inset-x-0 top-0 z-20 h-20 bg-linear-to-b from-black/40 to-transparent"
		/>

		<Transition
			enter-active-class="transition-opacity duration-150"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-300"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				v-if="seekFeedback === 'backward' && !isHolding"
				class="pointer-events-none absolute left-6 top-1/2 z-40 -translate-y-1/2"
			>
				<IconMaterialSymbolsLightReplay10
					class="size-8 text-white/80 drop-shadow-lg"
				/>
			</div>
		</Transition>

		<Transition
			enter-active-class="transition-opacity duration-150"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-300"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				v-if="seekFeedback === 'forward' && !isHolding"
				class="pointer-events-none absolute right-6 top-1/2 z-40 -translate-y-1/2"
			>
				<IconMaterialSymbolsLightForward10
					class="size-8 text-white/80 drop-shadow-lg"
				/>
			</div>
		</Transition>

		<Transition
			enter-active-class="transition-opacity duration-200"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition-opacity duration-200"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<div
				v-if="isHolding"
				class="pointer-events-none absolute inset-x-0 bottom-8 z-50 flex justify-center"
			>
				<span class="text-sm font-semibold text-white drop-shadow-lg">
					Velocidade 2x ▶▶
				</span>
			</div>
		</Transition>
	</div>
</template>
